import { FunctionComponent, useEffect, useState } from 'react';
import { InsightsDetailTable } from './InsightsDetailTable';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { useApiClientWrapper, useMarketplace } from 'hooks';
import { Period } from '@spike/model';
import moment from 'moment-timezone';
import { useTimeZone } from 'hooks';
import Header from '../../components/Header';
import {
    BookingsInsightsReportState,
    BookingsInsightsReportStatus
} from 'reducers/bookingsInsightsReport/BookingsInsightsReportState';
import { BookingsInsightsReport } from 'model/BookingsReport';
import { getBookingsInsightsReport } from 'actions/insights/bookings/bookingsInsights/BookingsInsightsReportActions';
import styles from './BookingsInsights.module.css';

interface BookingsInsightsProps {
    description?: string;
}

export const BookingsInsights: FunctionComponent<BookingsInsightsProps> = props => {
    const dispatch = useDispatch();

    const timeZone = useTimeZone();

    const insightsLoading = useSelector<RootState, boolean>(state => state.bookingsInsightsReport.loading);

    const insightsStatus = useSelector<RootState, BookingsInsightsReportStatus>(
        state => state.bookingsInsightsReport.status
    );

    const insightsReport = useSelector<RootState, BookingsInsightsReportState>(state => state.bookingsInsightsReport);

    const [period, setPeriod] = useState<Period | null>({
        from: moment().tz(timeZone).startOf('month'),
        to: moment().tz(timeZone).endOf('month')
    });

    const [insightsReportConverted, setInsightsReportConverted] = useState<BookingsInsightsReport | null>(null);

    const apiClientWrapper = useApiClientWrapper();
    const marketplace = useMarketplace();

    // export
    const insightsCsvHeaders = [
        'Total Upcoming',
        'Total Upcoming (%)',
        'Total Completed',
        'Total Completed (%)',
        'Total Cancelled',
        'Total Cancelled (%)',
        'No Show',
        'No Show (%)',
        'Online Booking',
        'Online Booking (%)',
        'Business-Booked',
        'Business-Booked (%)',
        'New Clients',
        'New Clients (%)',
        'Returning Clients',
        'Returning Clients (%)'
    ];

    const dataToExport = [
        [
            `${insightsReportConverted?.totalUpcoming}`,
            `${insightsReportConverted?.percentUpcoming}`,
            `${insightsReportConverted?.totalCompleted}`,
            `${insightsReportConverted?.percentCompleted}`,
            `${insightsReportConverted?.totalCancelled}`,
            `${insightsReportConverted?.percentCancelled}`,
            `${insightsReportConverted?.totalNoShow}`,
            `${insightsReportConverted?.percentNoShow}`,
            `${insightsReportConverted?.totalOnlineBooking}`,
            `${insightsReportConverted?.percentOnlineBooking}`,
            `${insightsReportConverted?.totalBusinessBooked}`,
            `${insightsReportConverted?.percentBusinessBooked}`,
            `${insightsReportConverted?.totalNewClients}`,
            `${insightsReportConverted?.percentNewClients}`,
            `${insightsReportConverted?.totalReturningClients}`,
            `${insightsReportConverted?.percentReturningClients}`
        ]
    ];

    const exportFile = {
        name: `Booking Insights - ${marketplace.basics.businessName}.csv`,
        dataInMemory: true,
        dataToExport: dataToExport,
        headers: insightsCsvHeaders
    };

    const calendar = {
        optionSelected: 'This month'
    };

    const [retryAttempt, setRetryAttempt] = useState(0);

    useEffect(() => {
        dispatch(getBookingsInsightsReport(period?.from.format('YYYY/MM/DD'), period?.to.format('YYYY/MM/DD')));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period]);

    useEffect(() => {
        if (insightsStatus === BookingsInsightsReportStatus.GettingSuccess) {
            setInsightsReportConverted(insightsReport.insights);
        }
        if (insightsStatus === BookingsInsightsReportStatus.Error) {
            setPeriod({
                from: moment().tz(timeZone).startOf('month'),
                to: moment().tz(timeZone).endOf('month')
            });
            setInsightsReportConverted(null);
            setRetryAttempt(prev => prev + 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [insightsStatus]);

    const handleChangePeriod = (dateFilter: Period | null, allTime: boolean) => {
        if (allTime) {
            setPeriod(null);
        } else {
            setPeriod({
                from: dateFilter?.from || moment(),
                to: dateFilter?.to || moment()
            });
        }
    };

    return (
        <div
            className={styles.container}
            key={retryAttempt}
        >
            <div>
                <span className={styles.description}>{props.description}</span>
            </div>
            <div className={styles.mobileWrapper}>
                <Header
                    period={period}
                    onChangePeriod={handleChangePeriod}
                    exportFile={exportFile}
                    calendar={calendar}
                    hideAllTime={true}
                />
            </div>
            <InsightsDetailTable
                loading={insightsLoading}
                insights={insightsReportConverted}
            />
        </div>
    );
};

export default BookingsInsights;
